/** @jsxImportSource @emotion/react */
import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { ApiOrgProperty, ApiPropertyProject } from '@kaiizen/kai-pro-api';

import { NOTES_CHARACTER_LIMIT } from '@/Client/noteConstants';
import { type NewPropertyProjectValues, useCreatePropertyProject } from '@/Client/Partials/Properties/useCreatePropertyProject';
import { useToast } from '@/Client/State/ToastContext';
import { ModalShell } from '@/Client/Widgets/Modal';
import { DEFAULT_NEW_JOB_MODAL_STYLES, type NewJobModalStyles } from './newJobModalStyles';
import { formatShortDate } from './propertyOverviewLib';

export const CREATE_JOB_LABEL = 'Create job';

/** A job left unnamed is named for its street and the day it was made: "2525 McDonald Way – Oct 21, 2026". */
export const getDefaultJobName = (address: string | undefined, today: Date = new Date()) =>
  `${(address ?? '').split(',')[0].trim() || 'New job'} – ${formatShortDate(today)}`;

/**
 * Creating a job (a project) at a property, in three short groups - the job, getting in, and who
 * is on it - with the same fields, rules and create as the legacy flyout (`useCreatePropertyProject`).
 * Once a job type is chosen, the template field says which market default applied, or plainly that
 * the market has none, rather than leaving an unexplained empty required field.
 */
export const NewJobModal = ({
  visible,
  onClose,
  property,
  projectMutators,
  styles = DEFAULT_NEW_JOB_MODAL_STYLES,
}: {
  visible: boolean;
  onClose: () => void;
  property: ApiOrgProperty;
  projectMutators: { create: (data: any) => Promise<ApiPropertyProject> };
  styles?: NewJobModalStyles;
}) => {
  const { showErrorToast } = useToast();
  const {
    status,
    blocker,
    orgMarket,
    projectTypeOptions,
    templateOptions,
    defaultScanTemplate,
    userOptions,
    managerOptions,
    getManagerLabel,
    initialValues,
    validate,
    createProject,
  } = useCreatePropertyProject(property, projectMutators);
  const [values, setValues] = useState<NewPropertyProjectValues>(initialValues);
  const [touched, setTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  // The disabled button stops a second click; this stops any other second submit while the first runs.
  const createInFlightRef = useRef(false);

  // A fresh form each time it opens, and once the defaults (template, managers) have loaded.
  const defaultsKey = `${visible}|${status}|${initialValues.scanTemplateId}|${initialValues.managerIds.join(',')}`;
  useEffect(() => {
    if (!visible) return;
    setValues(initialValues);
    setTouched(false);
  }, [defaultsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const errors = validate(values);
  const canCreate = Object.keys(errors).length === 0 && !creating;
  const set = <Key extends keyof NewPropertyProjectValues>(key: Key, value: NewPropertyProjectValues[Key]) =>
    setValues(current => ({ ...current, [key]: value }));
  const errorFor = (key: keyof NewPropertyProjectValues) => touched && errors[key] ? <span css={styles.error}>{errors[key]}</span> : null;

  const submit = async () => {
    setTouched(true);
    if (Object.keys(errors).length > 0 || createInFlightRef.current) return;
    createInFlightRef.current = true;
    setCreating(true);
    try {
      await createProject({ ...values, name: values.name.trim() || getDefaultJobName(property.address) });
      onClose();
    } catch (error) {
      showErrorToast(error, 'create the job');
    } finally {
      createInFlightRef.current = false;
      setCreating(false);
    }
  };

  const field = (
    id: string,
    label: string,
    control: ReactNode,
    { optional = false, wide = false, hint }: { optional?: boolean; wide?: boolean; hint?: ReactNode } = {},
  ) => (
    <div css={[styles.field, wide && styles.wideField]}>
      <label htmlFor={id} css={styles.label}>
        {label}
        {optional ? <span css={styles.optional}>optional</span> : null}
      </label>
      {control}
      {hint}
    </div>
  );

  const templateHint = !values.projectType ? null : (
    <span css={styles.hint} data-testid='new-job-template-hint'>
      {defaultScanTemplate
        ? `${orgMarket?.name ?? 'The market'}'s default template: ${defaultScanTemplate.name}.`
        : `${orgMarket?.name ?? 'This market'} has no default template. Choose one.`}
    </span>
  );
  // Every chosen manager gets a chip, including a preselected one from outside the market.
  const managers = values.managerIds.map(id => ({ value: id, label: getManagerLabel(id) }));
  const unchosenManagers = managerOptions.filter(option => !values.managerIds.includes(option.value));

  return (
    <ModalShell visible={visible} onClose={onClose} contentMaxWidth='640px' closeOnBackdropClick={!creating}>
      <div css={styles.dialog} role='dialog' aria-modal='true' aria-labelledby='new-job-title'>
        <div css={styles.header}>
          <div>
            <h2 id='new-job-title' css={styles.title}>New job</h2>
            <div css={styles.subtitle}>{property.address}</div>
          </div>
          <button type='button' css={styles.close} aria-label='Close' onClick={onClose}>×</button>
        </div>

        {status === 'loading'
          ? (
            <div css={styles.body}>
              <span css={styles.hint}>Loading…</span>
            </div>
          )
          : blocker
          ? (
            <div css={styles.body}>
              <div css={styles.blocker} role='status'>
                {blocker.message}
                {blocker.actions.map(action => (
                  <button key={action.label} type='button' css={styles.shared.button} onClick={action.onClick}>{action.label}</button>
                ))}
              </div>
            </div>
          )
          : (
            <form
              css={styles.form}
              onSubmit={event => {
                event.preventDefault();
                void submit();
              }}
            >
              <div css={styles.body}>
                <fieldset css={styles.group}>
                  <legend css={styles.groupTitle}>The job</legend>
                  <div css={styles.fields}>
                    {field(
                      'new-job-type',
                      'Job type',
                      <select id='new-job-type' css={styles.input} value={values.projectType} onChange={event => set('projectType', event.target.value)}>
                        <option value=''>Select a type…</option>
                        {projectTypeOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>,
                      { hint: errorFor('projectType') },
                    )}
                    {field(
                      'new-job-budget',
                      'Budget',
                      <input
                        id='new-job-budget'
                        css={styles.input}
                        type='number'
                        inputMode='decimal'
                        placeholder='0.00'
                        value={values.budget}
                        onChange={event => set('budget', event.target.value === '' ? '' : event.target.value as unknown as number)}
                      />,
                      { optional: true, hint: errorFor('budget') },
                    )}
                    {field(
                      'new-job-name',
                      'Job name',
                      <input
                        id='new-job-name'
                        css={styles.input}
                        placeholder='Defaults to the address and today’s date'
                        value={values.name}
                        onChange={event => set('name', event.target.value)}
                      />,
                      { optional: true, wide: true },
                    )}
                    {field(
                      'new-job-scope-due',
                      'Scope due date',
                      <input
                        id='new-job-scope-due'
                        css={styles.input}
                        type='date'
                        value={String(values.scanDueDate ?? '')}
                        onChange={event => set('scanDueDate', event.target.value)}
                      />,
                    )}
                    {field(
                      'new-job-end',
                      'Job end date',
                      <input
                        id='new-job-end'
                        css={styles.input}
                        type='date'
                        value={String(values.projectDueDate ?? '')}
                        onChange={event => set('projectDueDate', event.target.value)}
                      />,
                    )}
                    {field(
                      'new-job-template',
                      'Template',
                      <select
                        id='new-job-template'
                        css={styles.input}
                        value={values.scanTemplateId}
                        onChange={event => set('scanTemplateId', event.target.value)}
                      >
                        <option value=''>Select a template…</option>
                        {templateOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>,
                      { wide: true, hint: errorFor('scanTemplateId') ?? templateHint },
                    )}
                  </div>
                </fieldset>

                <fieldset css={styles.group}>
                  <legend css={styles.groupTitle}>Getting in</legend>
                  <div css={styles.fields}>
                    {field(
                      'new-job-door',
                      'Door code',
                      <input
                        id='new-job-door'
                        css={styles.input}
                        placeholder='1234'
                        value={values.doorCode}
                        onChange={event => set('doorCode', event.target.value)}
                      />,
                      { optional: true },
                    )}
                    {field(
                      'new-job-gate',
                      'Gate code',
                      <input
                        id='new-job-gate'
                        css={styles.input}
                        placeholder='4321'
                        value={values.gateCode}
                        onChange={event => set('gateCode', event.target.value)}
                      />,
                      { optional: true },
                    )}
                    {field(
                      'new-job-notes',
                      'Dispatch notes',
                      <textarea
                        id='new-job-notes'
                        css={styles.input}
                        rows={3}
                        maxLength={NOTES_CHARACTER_LIMIT}
                        placeholder='Anything the crew needs before they arrive - where the lockbox is, when not to knock.'
                        value={values.description ?? ''}
                        onChange={event => set('description', event.target.value)}
                      />,
                      {
                        optional: true,
                        wide: true,
                        hint: <span css={styles.hint}>{(values.description ?? '').length} / {NOTES_CHARACTER_LIMIT}</span>,
                      },
                    )}
                  </div>
                </fieldset>

                <fieldset css={styles.group}>
                  <legend css={styles.groupTitle}>Who is on it</legend>
                  <div css={styles.fields}>
                    {field(
                      'new-job-managers',
                      'Assigned managers',
                      <>
                        <select
                          id='new-job-managers'
                          css={styles.input}
                          value=''
                          onChange={event => event.target.value && set('managerIds', [...values.managerIds, event.target.value])}
                        >
                          <option value=''>Add a manager…</option>
                          {unchosenManagers.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        {managers.length
                          ? (
                            <div css={styles.chips}>
                              {managers.map(manager => (
                                <span key={manager.value} css={styles.chip}>
                                  {manager.label}
                                  <button
                                    type='button'
                                    css={styles.chipRemove}
                                    aria-label={`Remove ${manager.label}`}
                                    onClick={() => set('managerIds', values.managerIds.filter(id => id !== manager.value))}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )
                          : null}
                      </>,
                      { wide: true, hint: errorFor('managerIds') },
                    )}
                    {field(
                      'new-job-field-user',
                      'Field user',
                      <select
                        id='new-job-field-user'
                        css={styles.input}
                        value={values.assignedUserId}
                        onChange={event => set('assignedUserId', event.target.value)}
                      >
                        <option value=''>Select a field user…</option>
                        {userOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>,
                      { hint: errorFor('assignedUserId') },
                    )}
                  </div>
                </fieldset>
              </div>

              <div css={styles.footer}>
                <button type='button' css={styles.shared.button} disabled={creating} onClick={onClose}>Cancel</button>
                <button type='submit' css={[styles.shared.button, styles.shared.primaryButton]} disabled={!canCreate}>
                  {creating ? 'Creating…' : CREATE_JOB_LABEL}
                </button>
              </div>
            </form>
          )}
      </div>
    </ModalShell>
  );
};
